import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { KeyRound } from "lucide-react";

import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/core/components/ui/card";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import type { DeviceSessionKeyExchangeResponseDto } from "@/features/admin/api/dto";
import { useExchangeDeviceSessionKey } from "@/features/admin/api/use-exchange-device-session-key";
import { useAdminTranslation } from "@/features/admin/i18n/use-admin-translation";
import {
  createDeviceSessionKeyExchangeSchema,
  type DeviceSessionKeyExchangeFormValues,
} from "@/features/admin/schemas/device-session-key.schema";

const EMPTY_VALUES: DeviceSessionKeyExchangeFormValues = {
  keycloakUserId: "",
  deviceId: "",
  clientPublicKeyBase64: "",
  ttlHours: 12,
};

/**
 * ⚙️ Écran minimal d'outillage : échange d'une clé de session device
 * (`POST /DeviceSessionKeys/exchange` — endpoint gateway, §6.1, authentifié
 * sans rôle particulier). Affiche la clé publique serveur et son expiration.
 */
export function DeviceSessionKeyExchangeCard() {
  const { t } = useAdminTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : schéma zod traduit reconstruit sur changement de langue (règle projet).
  const schema = useMemo(() => createDeviceSessionKeyExchangeSchema(), [i18n.language]);

  const exchange = useExchangeDeviceSessionKey();
  const [result, setResult] =
    useState<DeviceSessionKeyExchangeResponseDto | null>(null);

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      exchange.mutate(parsed.data, {
        onSuccess: (data) => {
          notifSilence(t("admin.deviceKeys.feedback.exchanged"));
          setResult(data);
        },
        onError: (error) => notifSilenceError(error.message),
      });
    },
  });

  const fieldError =
    (name: keyof DeviceSessionKeyExchangeFormValues) =>
    (props: { value: unknown }) => {
      const shape = schema.shape[name];
      const result = shape.safeParse(props.value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <KeyRound className="h-4 w-4 text-accent-dark" aria-hidden />
          {t("admin.deviceKeys.title")}
        </CardTitle>
        <CardDescription>{t("admin.deviceKeys.description")}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <form
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.Field
            name="keycloakUserId"
            validators={{ onChange: fieldError("keycloakUserId") }}
          >
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="dsk-user">
                  {t("admin.deviceKeys.form.keycloakUserId")}
                </Label>
                <Input
                  id="dsk-user"
                  className="font-mono notranslate"
                  placeholder={t(
                    "admin.deviceKeys.form.keycloakUserIdPlaceholder"
                  )}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field
            name="deviceId"
            validators={{ onChange: fieldError("deviceId") }}
          >
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="dsk-device">
                  {t("admin.deviceKeys.form.deviceId")}
                </Label>
                <Input
                  id="dsk-device"
                  className="font-mono notranslate"
                  placeholder={t("admin.deviceKeys.form.deviceIdPlaceholder")}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field
            name="clientPublicKeyBase64"
            validators={{ onChange: fieldError("clientPublicKeyBase64") }}
          >
            {(field) => (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="dsk-key">
                  {t("admin.deviceKeys.form.clientPublicKey")}
                </Label>
                <Input
                  id="dsk-key"
                  className="font-mono notranslate"
                  placeholder={t(
                    "admin.deviceKeys.form.clientPublicKeyPlaceholder"
                  )}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field
            name="ttlHours"
            validators={{ onChange: fieldError("ttlHours") }}
          >
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="dsk-ttl">
                  {t("admin.deviceKeys.form.ttlHours")}
                </Label>
                <Input
                  id="dsk-ttl"
                  type="number"
                  min={1}
                  max={720}
                  className="notranslate"
                  placeholder={t("admin.deviceKeys.form.ttlHoursPlaceholder")}
                  value={field.state.value ?? ""}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <div className="flex items-end">
            <Button type="submit" disabled={exchange.isPending}>
              {exchange.isPending ? <ButtonSpinner /> : null}
              {t("admin.deviceKeys.form.submit")}
            </Button>
          </div>
        </form>

        {result ? (
          <div className="flex flex-col gap-2 rounded-md border border-success/40 bg-success-muted px-3 py-2">
            <div>
              <p className="text-sm text-muted-foreground">
                {t("admin.deviceKeys.result.serverPublicKey")}
              </p>
              <NoTranslate as="span" className="break-all font-mono text-xs">
                {result.serverPublicKeyBase64}
              </NoTranslate>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                {t("admin.deviceKeys.result.expiresAt")}
              </p>
              <NoTranslate as="span" className="font-mono text-xs">
                {formatDateWithFallback(result.expiresAt, "—")}
              </NoTranslate>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
